import { sanitizeVoiceover } from "./voiceoverSanitize";

/**
 * 尾句裁切提交时明示(0817 老板拍板)—— ai #18 硬裁逻辑的 TS 镜像。
 *
 * ai 编排层会把每镜口播**确定性截断**到 round(镜时长 × 7.28) 字以内
 * (`multi_shot._cap_voiceovers_by_duration`,#18 防口播超镜长盖下一段;
 * G-04:只保留装得下的完整小句,首小句就超限才硬切,结尾悬着的标点剥掉;
 * 1007 v2 整句优先:小句累加后再退到最后一个句末标点,前提是退后仍 ≥ ceil(limit×0.6) 字)。
 * 用户看到的现象是「最后一句没念」——本模块在提交前算出**同一刀**会落在哪,
 * 明示「预计裁至『……』(第X句)」,知情同意后照裁。
 *
 * ★同源纪律:常量与切分/剥尾语义必须与 ai 仓 `app/modules/video/application/multi_shot.py`
 *   逐字对齐(7.28 / 小句边界字符集 / 悬尾字符集)。测试用例直接搬 ai 的
 *   `tests/video/test_voiceover_cap.py` 固定夹具当跨仓同源锁 —— 那边改口径,这边锁红。
 * ★软警戒线 5.5(cameraLexicon.VOICEOVER_DENSITY_MAX)是「会念糊」的引导阈值;
 *   这里的 7.28 是「会被裁」的硬事实,两者语义不同,别合并。
 */

/**
 * 硬上限语速(字/秒)—— ai `_VOICEOVER_MAX_CHARS_PER_SEC` 同值。
 * ★0913 校准:5.85 → 7.28(= TTS 实测读速 5.6 × 语速上限 1.3;5.85 是 0826 旧读速
 * 4.5 时代的遗留,0826 重标读速时漏了这枚,导致「编排说装得下、渲染又裁一刀」)。
 */
export const VOICEOVER_HARD_CAP_CHARS_PER_SEC = 7.28;

// 小句边界(切分但保留分隔符)—— ai `_VO_CLAUSE_BOUNDARY` 同字符集。
const CLAUSE_BOUNDARY = /(?<=[。！？；，、.!?;,])/;

// 悬尾符号集 —— ai `_VO_DANGLING_TAIL` 同字符集(截断后结尾挂着的标点/连接符剥掉)。
const DANGLING_TAIL = "。！？；，、.!?;,/／·—-:：&＆+＋~～… \t";

// ai 侧是 Python round = 银行家舍入(half-to-even):round(58.5)=58 而 JS Math.round=59。
// 10s 镜(5.85×10=58.5)就会差 1 字 —— 必须按 Python 语义舍入才叫同源。
const roundHalfToEven = (x: number): number => {
  const floor = Math.floor(x);
  const frac = x - floor;
  if (Math.abs(frac - 0.5) < 1e-9) return floor % 2 === 0 ? floor : floor + 1;
  return Math.round(x);
};

/** 本镜按硬上限约可念多少字(= ai 截断上限 round(dur × 7.28));dur ≤0 → 0(不截)。 */
export function voiceoverCharLimit(durationSec: number | null | undefined): number {
  const dur = durationSec && durationSec > 0 ? durationSec : 0;
  return roundHalfToEven(dur * VOICEOVER_HARD_CAP_CHARS_PER_SEC);
}

/** 要把 `chars` 字全部念完,这一镜至少要几秒(整数秒;chars ≤0 → 0)。 */
export function voiceoverSecondsNeeded(chars: number): number {
  if (chars <= 0) return 0;
  let d = Math.max(1, Math.floor(chars / VOICEOVER_HARD_CAP_CHARS_PER_SEC));
  while (voiceoverCharLimit(d) < chars) d += 1;
  return d;
}

const stripDanglingTail = (s: string): string => {
  let end = s.length;
  while (end > 0 && DANGLING_TAIL.includes(s[end - 1])) end -= 1;
  return s.slice(0, end);
};

export interface VoiceoverCapPrediction {
  /** 清洗后的全文(去引号、换行折句读,见 voiceoverSanitize)—— 截断与字数都按它算。 */
  cleaned: string;
  /** ai 侧裁后将实际念出的文本(已剥悬尾)。 */
  capped: string;
  /** 将被截掉、不会念出也不会出字幕的那段原文(未截断时为空串)。 */
  dropped: string;
  /** `dropped` 按句末标点拆成的句子列表(给界面逐句列出)。 */
  droppedSentences: string[];
  /** 是否会发生裁切。 */
  cut: boolean;
  /** 保留到第几个小句(与裁切用同一套小句切分;首小句超限硬切时为 0)。 */
  keptClauses: number;
  /** 本镜字数上限 round(dur × 7.28)。 */
  limit: number;
}

// v2 整句优先的句末集合 —— ai `_VO_SENTENCE_END` 同字符集(注意不含半角句点 .,防小数/英文缩写)。
const SENTENCE_END_CHARS = "。！？；!?;…";
// 整句前缀最少要占上限的比例(ceil(limit × 0.6))。
const WHOLE_SENTENCE_MIN_RATIO = 0.6;

// 被截部分开头挂着的句读/空白剥掉再展示(「，认准3+3」→「认准3+3」)。
const LEADING_PUNCT_RE = /^[。！？；，、.!?;,:：…—\-~～·\s]+/;
// 被截部分按句末标点拆句(保留标点)。
const SENTENCE_END = /(?<=[。！？；.!?;])/;

const droppedPart = (cleaned: string, capped: string) => {
  const dropped = cleaned.slice(capped.length).replace(LEADING_PUNCT_RE, "");
  const droppedSentences = dropped
    .split(SENTENCE_END)
    .map((x) => x.trim())
    .filter((x) => x.replace(LEADING_PUNCT_RE, "").length > 0);
  return { dropped, droppedSentences };
};

/**
 * 预测 ai #18 对单镜口播的裁切结果。duration ≤0 或未超限 → 不裁(与 ai 行为一致)。
 * ★先清洗(sanitizeVoiceover,与 ai 侧同口径)再截断,字数/裁点都按清洗后的文本。
 */
export function predictVoiceoverCap(
  voiceover: string | null | undefined,
  durationSec: number | null | undefined
): VoiceoverCapPrediction {
  const text = sanitizeVoiceover(voiceover);
  const limit = voiceoverCharLimit(durationSec);
  const base = { cleaned: text, dropped: "", droppedSentences: [] as string[], limit };
  if (limit <= 0 || text.length <= limit) {
    return { ...base, capped: text, cut: false, keptClauses: 0 };
  }
  let kept = "";
  let clauses = 0;
  for (const clause of text.split(CLAUSE_BOUNDARY)) {
    if (kept.length + clause.length > limit) break;
    kept += clause;
    clauses += 1;
  }
  // v2 整句优先(1007,ai 逐字同口径):截断发生且 kept 非空时,退到 kept 里最后一个句末标点;
  // 退后的整句前缀至少要有 ceil(limit×0.6) 字,太短就保持小句级结果(别为整句丢掉大半内容)。
  if (kept) {
    let pos = -1;
    for (let i = kept.length - 1; i >= 0; i -= 1) {
      if (SENTENCE_END_CHARS.includes(kept[i])) {
        pos = i;
        break;
      }
    }
    if (pos >= 0 && pos + 1 >= Math.ceil(limit * WHOLE_SENTENCE_MIN_RATIO)) {
      kept = kept.slice(0, pos + 1);
      clauses = kept.split(CLAUSE_BOUNDARY).filter(Boolean).length;
    }
  }
  // 首小句就超限(句内无句读)→ 硬切,最后手段(与 ai 一致)
  const capped = stripDanglingTail(kept ? kept : text.slice(0, limit));
  return {
    ...base,
    ...droppedPart(text, capped),
    capped,
    cut: true,
    keptClauses: kept ? clauses : 0,
  };
}

/** 「想全部念出来:……」的指引句(缩短口播 / 拉长时长,给出具体字数与秒数)。 */
export function voiceoverFixAdvice(p: VoiceoverCapPrediction): string {
  const need = voiceoverSecondsNeeded(p.cleaned.length);
  return `想全部念出来:把口播缩短到 ${p.limit} 字以内,或把时长拉长到 ${need} 秒以上。`;
}

export interface VoiceoverCutNotice {
  /** 0 起镜序。 */
  shotIndex: number;
  prediction: VoiceoverCapPrediction;
}

/** 整单预测:返回将被裁的镜列表(空数组 = 无裁切,不用打扰用户)。 */
export function predictVoiceoverCuts(
  voiceovers: (string | null)[] | null | undefined,
  durations: (number | null)[] | null | undefined
): VoiceoverCutNotice[] {
  if (!voiceovers || voiceovers.length === 0) return [];
  const out: VoiceoverCutNotice[] = [];
  voiceovers.forEach((vo, i) => {
    const prediction = predictVoiceoverCap(vo, durations?.[i] ?? 0);
    if (prediction.cut) out.push({ shotIndex: i, prediction });
  });
  return out;
}

/** 明示文案:「镜 N:预计裁至『…尾部片段』(第X句)」—— 被截掉的原文另由 droppedSentences 列出。 */
export function voiceoverCutLine(n: VoiceoverCutNotice): string {
  const tail =
    n.prediction.capped.length > 18 ? `…${n.prediction.capped.slice(-18)}` : n.prediction.capped;
  const at = n.prediction.keptClauses > 0 ? `(第${n.prediction.keptClauses}句末)` : "(句中硬切)";
  return `镜 ${n.shotIndex + 1}:预计裁至『${tail}』${at}`;
}

/**
 * 「太稀」提示(W4 批,2026-09-13)—— 超密红提示(上面 `VOICEOVER_HARD_CAP_CHARS_PER_SEC`)
 * 的另一半:文案写太少,这一镜念完会剩一大截留白。
 *
 * TTS 实测读速基准(字/秒)—— 与 `shotBudget.ts` 注释里的 `BASE_CHARS_PER_SEC(5.6 实测)`
 * 同源(那边只在注释里出现过,没导出常量;这里补成可复用的真值)。乘以 `voiceSpeechRate`
 * 得到某语速下的实际读速。
 */
export const VOICEOVER_TTS_BASE_CHARS_PER_SEC = 5.6;

// 去标点字符集(估算"能读多久"专用 —— 标点不占发音时长)。
// ★与上面硬裁的小句边界/悬尾字符集是**不同用途**的两张表,别合并:那两张表决定"切在哪",
//   这张表决定"这段字大概要念几秒",语义不同,常量也不共享。
const PUNCTUATION_RE = /[，。、！？；：""''《》【】（）()[\]{}.,!?;:'"…—\-~～·\s]/g;

/** 本镜口播「按语速估算能念多久」(去标点字数 / (5.6 × 语速))。rate 缺省 / ≤0 按 1 处理。 */
export function voiceoverReadableSec(
  voiceover: string | null | undefined,
  rate?: number | null
): number {
  const stripped = (voiceover ?? "").replace(PUNCTUATION_RE, "");
  const r = rate && rate > 0 ? rate : 1;
  return stripped.length / (VOICEOVER_TTS_BASE_CHARS_PER_SEC * r);
}
